import { ContentType } from 'common/src/commentThread/types'
import { INotecard } from 'common/src/notecards/types'
import {
    IMultiPartProblemBase,
    IPracticeProblemBase,
} from 'common/src/practiceProblems/types'
import { PracticeProblemType } from 'common/src/practiceProblems/consts'
import {
    extractTextFromStringifiedEditorState,
    tryExtractTextFromStringifiedEditorState,
    extractTextFromStringifiedEditorStateForAnnotationSelection,
} from 'magic-text-box-lexical/src/utils/extractTextFromStringifiedEditorState'
import {
    CommentTemplateVariable,
    LOOP_FOREACH_SUBPROBLEM_START,
    LOOP_FOREACH_SUBPROBLEM_END,
    AVAILABLE_VARIABLES_BY_CONTENT_TYPE,
} from 'common/src/api/contentFrontendVsContentBackend/commentResponseTemplate'
import { AnnotationSelection } from 'magic-text-box-lexical/src/context/AnnotatedContentContext/AnnotatedContent.types'
import { StringifiedMagicTextBox } from 'magic-text-box-lexical/src/components/types'

// ============================================================================
// Main Substitution Function
// ============================================================================

export interface SubstituteTemplateVariablesParams {
    template: string
    contentType: ContentType
    commentText: string
    includeSourceMaterial: boolean
    sourceMaterialText?: string
    // Content-specific data
    notecardData?: INotecard
    practiceProblemData?: IPracticeProblemBase
    outlineContent?: StringifiedMagicTextBox
    outlineSelection?: AnnotationSelection
    // Multi-part problem handling
    selectedSubProblemIndex?: number | null
}

export const substituteTemplateVariables = (
    params: SubstituteTemplateVariablesParams
): string => {
    let result = params.template

    // Replace universal variables
    result = result.replace(
        new RegExp(`{{${CommentTemplateVariable.STUDENT_COMMENT}}}`, 'g'),
        params.commentText
    )

    // Replace content-type-specific variables
    switch (params.contentType) {
        case ContentType.NOTECARD:
            result = substituteNotecardVariables(result, params.notecardData)
            break
        case ContentType.PRACTICE_PROBLEM:
            result = substitutePracticeProblemVariables(
                result,
                params.practiceProblemData,
                params.selectedSubProblemIndex
            )
            break
        case ContentType.OUTLINE:
        case ContentType.SUMMARY:
            result = substituteOutlineVariables(
                result,
                params.outlineContent,
                params.outlineSelection
            )
            break
    }

    // Handle source material
    const sourceMaterialVariable = `{{${CommentTemplateVariable.SOURCE_MATERIAL}}}`
    const hasSourceMaterialVariable = result.includes(sourceMaterialVariable)
    const shouldIncludeSourceMaterial =
        params.includeSourceMaterial && params.sourceMaterialText

    if (hasSourceMaterialVariable) {
        // Template has the variable - substitute it (or replace with empty if not including)
        result = result.replace(
            new RegExp(sourceMaterialVariable, 'g'),
            shouldIncludeSourceMaterial ? params.sourceMaterialText : ''
        )
    } else if (shouldIncludeSourceMaterial) {
        // Template doesn't have the variable but user wants source material - append at end
        result += `\n\n--- Relevant Background Information ---\n\n${params.sourceMaterialText}`
    }

    return result
}

// ============================================================================
// Content-Type-Specific Substitution Functions
// ============================================================================

const substituteNotecardVariables = (
    template: string,
    notecardData?: INotecard
): string => {
    if (!notecardData) {
        return template
    }

    let result = template

    const frontText = tryExtractTextFromStringifiedEditorState(
        notecardData.front
    )
    const backText = tryExtractTextFromStringifiedEditorState(notecardData.back)

    result = result.replace(
        new RegExp(`{{${CommentTemplateVariable.NOTECARD_FRONT}}}`, 'g'),
        frontText
    )
    result = result.replace(
        new RegExp(`{{${CommentTemplateVariable.NOTECARD_BACK}}}`, 'g'),
        backText
    )

    return result
}

const substitutePracticeProblemVariables = (
    template: string,
    practiceProblemData?: IPracticeProblemBase,
    selectedSubProblemIndex?: number | null
): string => {
    if (!practiceProblemData) {
        return template
    }

    let result = template

    // Extract question and solution text
    const questionText = tryExtractTextFromStringifiedEditorState(
        practiceProblemData.question
    )
    const solutionText = tryExtractTextFromStringifiedEditorState(
        practiceProblemData.solution
    )

    // Replace basic problem variables
    result = result.replace(
        new RegExp(`{{${CommentTemplateVariable.QUESTION}}}`, 'g'),
        questionText
    )
    result = result.replace(
        new RegExp(`{{${CommentTemplateVariable.SOLUTION}}}`, 'g'),
        solutionText
    )

    // Handle metadata (type, points, etc.)
    const metadata = `Type: ${practiceProblemData.type}, Points: ${practiceProblemData.points}`
    result = result.replace(
        new RegExp(`{{${CommentTemplateVariable.METADATA}}}`, 'g'),
        metadata
    )

    // Handle multi-part problem loop syntax
    if (practiceProblemData.type === PracticeProblemType.MULTI_PART) {
        result = substituteMultiPartProblemLoop(
            result,
            practiceProblemData as IMultiPartProblemBase,
            selectedSubProblemIndex
        )
    } else {
        // Remove loop blocks for non-multi-part problems
        result = removeLoopBlocks(result)
    }

    return result
}

const substituteOutlineVariables = (
    template: string,
    outlineContent?: StringifiedMagicTextBox,
    outlineSelection?: AnnotationSelection
): string => {
    if (!outlineContent) {
        return template
    }

    let result = template

    // Extract highlighted text if selection exists
    if (outlineSelection) {
        const highlightedText =
            extractTextFromStringifiedEditorStateForAnnotationSelection(
                outlineContent,
                outlineSelection
            ) || ''
        result = result.replace(
            new RegExp(`{{${CommentTemplateVariable.HIGHLIGHTED_TEXT}}}`, 'g'),
            highlightedText
        )
    } else {
        // No selection, replace with empty string
        result = result.replace(
            new RegExp(`{{${CommentTemplateVariable.HIGHLIGHTED_TEXT}}}`, 'g'),
            ''
        )
    }

    // Extract entire outline text
    const entireOutlineText =
        tryExtractTextFromStringifiedEditorState(outlineContent)
    result = result.replace(
        new RegExp(`{{${CommentTemplateVariable.ENTIRE_OUTLINE}}}`, 'g'),
        entireOutlineText
    )

    return result
}

// ============================================================================
// Multi-Part Problem Loop Handling
// ============================================================================

const substituteMultiPartProblemLoop = (
    template: string,
    multiPartProblem: IMultiPartProblemBase,
    selectedSubProblemIndex?: number | null
): string => {
    // Find all loop blocks
    const loopRegex = new RegExp(
        `${LOOP_FOREACH_SUBPROBLEM_START}([\\s\\S]*?)${LOOP_FOREACH_SUBPROBLEM_END}`,
        'g'
    )

    return template.replace(loopRegex, (match, loopContent) => {
        // Determine which subproblems to process
        const subProblemsToProcess =
            selectedSubProblemIndex !== null &&
            selectedSubProblemIndex !== undefined
                ? [multiPartProblem.subProblems[selectedSubProblemIndex]]
                : multiPartProblem.subProblems

        // Process each subproblem
        return subProblemsToProcess
            .map((subProblem, index) => {
                const actualIndex =
                    selectedSubProblemIndex !== null &&
                    selectedSubProblemIndex !== undefined
                        ? selectedSubProblemIndex
                        : index

                const label = String.fromCharCode(65 + actualIndex) // A, B, C, etc.
                const subQuestionText =
                    tryExtractTextFromStringifiedEditorState(
                        subProblem.question
                    )
                const subSolutionText =
                    tryExtractTextFromStringifiedEditorState(
                        subProblem.solution
                    )

                let processedContent = loopContent

                processedContent = processedContent.replace(
                    new RegExp(
                        `{{${CommentTemplateVariable.SUBPROBLEM_LABEL}}}`,
                        'g'
                    ),
                    label
                )
                processedContent = processedContent.replace(
                    new RegExp(
                        `{{${CommentTemplateVariable.SUBPROBLEM_QUESTION}}}`,
                        'g'
                    ),
                    subQuestionText
                )
                processedContent = processedContent.replace(
                    new RegExp(
                        `{{${CommentTemplateVariable.SUBPROBLEM_SOLUTION}}}`,
                        'g'
                    ),
                    subSolutionText
                )

                return processedContent
            })
            .join('')
    })
}

const removeLoopBlocks = (template: string): string => {
    const loopRegex = new RegExp(
        `${LOOP_FOREACH_SUBPROBLEM_START}[\\s\\S]*?${LOOP_FOREACH_SUBPROBLEM_END}`,
        'g'
    )
    return template.replace(loopRegex, '')
}

// ============================================================================
// Helper Functions
// ============================================================================

export const getAvailableVariablesForContentType = (
    contentType: ContentType
): CommentTemplateVariable[] => {
    return AVAILABLE_VARIABLES_BY_CONTENT_TYPE[contentType] || []
}

export const validateTemplate = (
    template: string,
    contentType: ContentType
): { isValid: boolean; unrecognizedVariables: string[] } => {
    const availableVariables = getAvailableVariablesForContentType(contentType)
    const variableRegex = /{{(\w+)}}/g
    const foundVariables: string[] = []
    let match

    while ((match = variableRegex.exec(template)) !== null) {
        foundVariables.push(match[1])
    }

    const unrecognizedVariables = foundVariables.filter(
        (variable) =>
            !availableVariables.includes(variable as CommentTemplateVariable) &&
            variable !== 'FOREACH_SUBPROBLEM' && // Allow loop syntax
            variable !== '/FOREACH_SUBPROBLEM'
    )

    return {
        isValid: unrecognizedVariables.length === 0,
        unrecognizedVariables,
    }
}
